import React, { createContext, useContext } from 'react';
import { useAuth } from './AuthContext';

const ApiContext = createContext();

export function ApiProvider({ children }) {
    const API_BASE_URL = '/api';

    // Helper function to add service binding header and authentication to all requests
    const fetchWithServiceBinding = async (url, options = {}) => {
        // Ensure headers object exists
        const headers = options.headers || {};

        // Add X-Service-Binding header
        headers['X-Service-Binding'] = 'true';

        // Add authentication header if token exists
        const token = localStorage.getItem('auth_token');
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }

        // Make the request
        const response = await fetch(url, {
            ...options,
            headers
        });

        // Check for 401 Unauthorized responses
        if (response.status === 401) {
            // Token is invalid, clear auth state and redirect to login
            localStorage.removeItem('auth_token');
            localStorage.removeItem('user_data');

            // Redirect to login page
            window.location.href = '/login';

            // Throw an error to stop further processing
            throw new Error('Authentication required. Redirecting to login...');
        }

        return response;
    };

    // Organizations API
    const organizationsApi = {
        getAll: async () => {
            const response = await fetchWithServiceBinding(`${API_BASE_URL}/organizations`);
            if (!response.ok) {
                throw new Error(`Failed to fetch organizations: ${response.status}`);
            }
            return response.json();
        },
        getById: async (id) => {
            const response = await fetchWithServiceBinding(`${API_BASE_URL}/organizations?id=${id}`);
            if (!response.ok) {
                throw new Error(`Failed to fetch organization: ${response.status}`);
            }
            return response.json();
        },
        getByPublicId: async (publicId) => {
            const response = await fetchWithServiceBinding(`${API_BASE_URL}/organizations?public_id=${publicId}`);
            if (!response.ok) {
                throw new Error(`Failed to fetch organization: ${response.status}`);
            }
            return response.json();
        },
        create: async (data) => {
            const response = await fetchWithServiceBinding(`${API_BASE_URL}/organizations`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(data),
            });
            if (!response.ok) {
                throw new Error(`Failed to create organization: ${response.status}`);
            }
            return response.json();
        },
        update: async (id, data) => {
            const response = await fetchWithServiceBinding(`${API_BASE_URL}/organizations?id=${id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(data),
            });
            if (!response.ok) {
                throw new Error(`Failed to update organization: ${response.status}`);
            }
            return response.json();
        },
        updateStatus: async (id, isActive) => {
            const response = await fetchWithServiceBinding(`${API_BASE_URL}/organizations/status`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ id, active: isActive }),
            });
            if (!response.ok) {
                throw new Error(`Failed to update organization status: ${response.status}`);
            }
            return response.json();
        },
        delete: async (id) => {
            const response = await fetchWithServiceBinding(`${API_BASE_URL}/organizations?id=${id}`, {
                method: 'DELETE',
            });
            if (!response.ok) {
                throw new Error(`Failed to delete organization: ${response.status}`);
            }
            return response.json();
        },

        // Get brand voice store for an organization
        getBrandVoiceStore: async (organizationId) => {
            const response = await fetchWithServiceBinding(`${API_BASE_URL}/organizations/brand-voice-store?organization_id=${organizationId}`);
            if (!response.ok) {
                throw new Error(`Failed to fetch brand voice store: ${response.status}`);
            }
            return response.json();
        },
    };

    // Users API
    const usersApi = {
        getAll: async () => {
            const response = await fetchWithServiceBinding(`${API_BASE_URL}/users`);
            if (!response.ok) {
                throw new Error(`Failed to fetch users: ${response.status}`);
            }
            return response.json();
        },
        getById: async (id) => {
            const response = await fetchWithServiceBinding(`${API_BASE_URL}/users?id=${id}`);
            if (!response.ok) {
                throw new Error(`Failed to fetch user: ${response.status}`);
            }
            return response.json();
        },
        getByPublicId: async (publicId) => {
            const response = await fetchWithServiceBinding(`${API_BASE_URL}/users?public_id=${publicId}`);
            if (!response.ok) {
                throw new Error(`Failed to fetch user: ${response.status}`);
            }
            return response.json();
        },
        create: async (data) => {
            const response = await fetchWithServiceBinding(`${API_BASE_URL}/users`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(data),
            });
            if (!response.ok) {
                throw new Error(`Failed to create user: ${response.status}`);
            }
            return response.json();
        },
        update: async (id, data) => {
            const response = await fetchWithServiceBinding(`${API_BASE_URL}/users?id=${id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(data),
            });
            if (!response.ok) {
                throw new Error(`Failed to update user: ${response.status}`);
            }
            return response.json();
        },
        delete: async (id) => {
            const response = await fetchWithServiceBinding(`${API_BASE_URL}/users?id=${id}`, {
                method: 'DELETE',
            });
            if (!response.ok) {
                throw new Error(`Failed to delete user: ${response.status}`);
            }
            return response.json();
        },
    };

    // Templates API
    const templatesApi = {
        getAll: async (organizationId) => {
            const url = organizationId
                ? `${API_BASE_URL}/templates?organization_id=${organizationId}`
                : `${API_BASE_URL}/templates`;

            const response = await fetchWithServiceBinding(url);
            if (!response.ok) {
                throw new Error(`Failed to fetch templates: ${response.status}`);
            }
            return response.json();
        },
        getById: async (id) => {
            const response = await fetchWithServiceBinding(`${API_BASE_URL}/templates?id=${id}`);
            if (!response.ok) {
                throw new Error(`Failed to fetch template: ${response.status}`);
            }
            return response.json();
        },
        getByPublicId: async (publicId) => {
            const response = await fetchWithServiceBinding(`${API_BASE_URL}/templates?public_id=${publicId}`);
            if (!response.ok) {
                throw new Error(`Failed to fetch template: ${response.status}`);
            }
            return response.json();
        },
        create: async (data) => {
            const response = await fetchWithServiceBinding(`${API_BASE_URL}/templates`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(data),
            });
            if (!response.ok) {
                const errorData = await response.json();
                const errorMessage = errorData.message || errorData.error || `Failed to create template: ${response.status}`;
                throw new Error(errorMessage);
            }
            return response.json();
        },
        update: async (id, data) => {
            const response = await fetchWithServiceBinding(`${API_BASE_URL}/templates?id=${id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(data),
            });
            if (!response.ok) {
                const errorData = await response.json();
                const errorMessage = errorData.message || errorData.error || `Failed to update template: ${response.status}`;
                throw new Error(errorMessage);
            }
            return response.json();
        },
        updateStatus: async (id, isActive) => {
            const response = await fetchWithServiceBinding(`${API_BASE_URL}/templates/status`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ id, active: isActive }),
            });
            if (!response.ok) {
                throw new Error(`Failed to update template status: ${response.status}`);
            }
            return response.json();
        },
        updateAutoCreatePages: async (id, autoCreatePages) => {
            const response = await fetchWithServiceBinding(`${API_BASE_URL}/templates/auto-create`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ id, auto_create_pages: autoCreatePages }),
            });
            if (!response.ok) {
                throw new Error(`Failed to update auto create pages setting: ${response.status}`);
            }
            return response.json();
        },
        delete: async (id) => {
            const response = await fetchWithServiceBinding(`${API_BASE_URL}/templates?id=${id}`, {
                method: 'DELETE',
            });
            if (!response.ok) {
                throw new Error(`Failed to delete template: ${response.status}`);
            }
            return response.json();
        },
    };

    // Pages API
    const pagesApi = {
        getAll: async (identifier, params) => {
            let url = `${API_BASE_URL}/pages`;
            const queryParams = new URLSearchParams();

            let actualParams = {};

            // Determine if identifier is templateId or the params object
            if (typeof identifier === 'string' || typeof identifier === 'number') {
                // First arg is templateId, second is params
                queryParams.set('template_id', identifier);
                actualParams = params || {};
            } else if (typeof identifier === 'object' && identifier !== null) {
                // First arg is params, no templateId provided
                actualParams = identifier;
            } // else: no args provided, actualParams remains {}

            // Add parameters from the actualParams object
            Object.entries(actualParams).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== '') {
                    queryParams.set(key, value);
                }
            });

            // Append query string if it exists
            const queryString = queryParams.toString();
            if (queryString) {
                url += `?${queryString}`;
            }

            const response = await fetchWithServiceBinding(url);
            if (!response.ok) {
                const errorText = await response.text();
                console.error("Failed to fetch pages. Status:", response.status, "Response:", errorText);
                throw new Error(`Failed to fetch pages: ${response.status}`);
            }
            return response.json();
        },
        getById: async (id) => {
            const response = await fetchWithServiceBinding(`${API_BASE_URL}/pages?id=${id}`);
            if (!response.ok) {
                throw new Error(`Failed to fetch page: ${response.status}`);
            }
            return response.json();
        },
        create: async (data) => {
            const response = await fetchWithServiceBinding(`${API_BASE_URL}/pages`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(data),
            });
            if (!response.ok) {
                throw new Error(`Failed to create page: ${response.status}`);
            }
            return response.json();
        },
        update: async (id, data) => {
            const response = await fetchWithServiceBinding(`${API_BASE_URL}/pages?id=${id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(data),
            });
            if (!response.ok) {
                throw new Error(`Failed to update page: ${response.status}`);
            }
            return response.json();
        },
        delete: async (id) => {
            const response = await fetchWithServiceBinding(`${API_BASE_URL}/pages?id=${id}`, {
                method: 'DELETE',
            });
            if (!response.ok) {
                throw new Error(`Failed to delete page: ${response.status}`);
            }
            return response.json();
        },

        // Generic method to fetch D1 page data
        getD1PageData: async (pageId, dataType = 'all') => {
            // Use query parameters with the new endpoint
            const response = await fetchWithServiceBinding(`${API_BASE_URL}/d1_pages/pages?cache_key=${encodeURIComponent(pageId)}&data=${dataType}`);
            if (!response.ok) {
                console.error(`Failed to fetch D1 data for page ${pageId}: ${response.status}`);
                return null;
            }
            return response.json();
        },

        // Convenience methods that use the generic endpoint
        getPageCreationDate: async (pageId) => {
            return await pagesApi.getD1PageData(pageId, 'creation_date');
        },

        getPageAnalytics: async (pageId) => {
            return await pagesApi.getD1PageData(pageId, 'analytics');
        },

        // New method for batch fetching metadata
        getBatchMetadata: async (cacheKeys, dataType = 'all') => {
            // Use the new batch endpoint with cache_keys parameter
            const response = await fetchWithServiceBinding(
                `${API_BASE_URL}/d1_pages/pages?cache_keys=${encodeURIComponent(cacheKeys)}&data=${dataType}`
            );

            if (!response.ok) {
                console.error(`Failed to fetch batch metadata for ${cacheKeys.length || 'multiple'} pages: ${response.status}`);
                throw new Error(`Failed to fetch batch metadata: ${response.status}`);
            }

            return response.json();
        },

        // These batch methods are kept for backward compatibility
        getCreationDates: async (pageIds) => {
            const response = await fetchWithServiceBinding(`${API_BASE_URL}/pages/creation-dates`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ pageIds }),
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch page creation dates: ${response.status}`);
            }

            return response.json();
        },

        getAnalytics: async (pageIds) => {
            const response = await fetchWithServiceBinding(`${API_BASE_URL}/pages/analytics`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ pageIds }),
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch page analytics: ${response.status}`);
            }

            return response.json();
        },

        // Get version status from KV metadata
        getVersionStatus: async (keys) => {
            const response = await fetchWithServiceBinding(`${API_BASE_URL}/version_status?keys=${encodeURIComponent(keys)}`);

            if (!response.ok) {
                throw new Error(`Failed to fetch version status: ${response.status}`);
            }

            return response.json();
        },

        // Update version status in KV metadata
        updateVersionStatus: async (data) => {
            const response = await fetchWithServiceBinding(`${API_BASE_URL}/update_version_status`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(data),
            });

            if (!response.ok) {
                throw new Error(`Failed to update version status: ${response.status}`);
            }

            return response.json();
        }
    };

    // Service Bindings API for direct worker-to-worker communication
    const serviceBindingsApi = {
        generatePages: async ({ url, keywords, shouldReprocess }) => {
            const response = await fetchWithServiceBinding(`${API_BASE_URL}/service-bindings/generate-pages`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    url,
                    keywords,
                    shouldReprocess
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || `Failed to generate pages: ${response.status}`);
            }

            return response.json();
        },

        regeneratePageWithLLM: async ({ url, keyword, instructions, versionId }) => {
            const response = await fetchWithServiceBinding(`${API_BASE_URL}/service-bindings/regenerate-page-with-llm`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    url,
                    keyword,
                    instructions,
                    versionId
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || `Failed to regenerate page: ${response.status}`);
            }

            return response.json();
        },

        deleteKeywordPhrases: async ({ url, keywordPhrases }) => {
            const response = await fetchWithServiceBinding(`${API_BASE_URL}/service-bindings/delete-keyword-phrases`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    url,
                    keywordPhrases
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || `Failed to delete keyword phrases: ${response.status}`);
            }

            return response.json();
        },

        checkJobStatus: async (jobId) => {
            const response = await fetchWithServiceBinding(`${API_BASE_URL}/service-bindings/job-status/${jobId}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({}),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || `Failed to check job status: ${response.status}`);
            }

            return response.json();
        },

        purgeTemplateCache: async ({ templateIdentifier }) => {
            const response = await fetchWithServiceBinding(`${API_BASE_URL}/service-bindings/purge-cache`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    templateIdentifier
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || `Failed to purge template cache: ${response.status}`);
            }

            return response.json();
        },

        deletePageVersion: async ({ versionKey }) => {
            const response = await fetchWithServiceBinding(`${API_BASE_URL}/service-bindings/delete-page-version`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    versionKey
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || `Failed to delete page version: ${response.status}`);
            }

            return response.json();
        },

        submitBrandVoiceDocument: async ({ url, title, organizationId }) => {
            const response = await fetchWithServiceBinding(`${API_BASE_URL}/service-bindings/brand-voice/submit`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    url,
                    title,
                    organizationId
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || `Failed to submit brand voice document: ${response.status}`);
            }

            return response.json();
        },

        // List origin sites for a vector store
        listOriginSites: async (vectorStoreId) => {
            const response = await fetchWithServiceBinding(`${API_BASE_URL}/service-bindings/brand-voice/list-origin-sites`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    vectorStoreId
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || `Failed to list origin sites: ${response.status}`);
            }

            return response.json();
        },

        deleteOriginSite: async (vectorStoreId, originSite) => {
            const response = await fetchWithServiceBinding(`${API_BASE_URL}/service-bindings/brand-voice/delete-origin-site`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    vectorStoreId,
                    originSite
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || `Failed to delete origin site: ${response.status}`);
            }

            return response.json();
        }
    };

    const api = {
        organizations: organizationsApi,
        users: usersApi,
        templates: templatesApi,
        pages: pagesApi,
        serviceBindings: serviceBindingsApi
    };

    return (
        <ApiContext.Provider value={api}>
            {children}
        </ApiContext.Provider>
    );
}

export function useApi() {
    const context = useContext(ApiContext);
    if (context === undefined) {
        throw new Error('useApi must be used within an ApiProvider');
    }
    return context;
} 